import type { RefObject } from "react";

const VISIBLE_MESSAGE_EDGE = {
  Top: "top",
  Bottom: "bottom",
} as const;

type VisibleMessageEdge =
  typeof VISIBLE_MESSAGE_EDGE[keyof typeof VISIBLE_MESSAGE_EDGE];

interface GetVisibleMessageIdInput {
  scrollRef: RefObject<HTMLDivElement | null>;
  visibleMessageIdsRef: RefObject<Set<string>>;
  messageElementsRef: RefObject<Map<string, HTMLElement>>;
  edge: VisibleMessageEdge;
}

interface VisibleMessageEdgeRule {
  initialValue: number;
  getCandidateValue: (rect: DOMRect) => number;
  isBetterCandidate: (candidateValue: number, bestValue: number) => boolean;
}

const VISIBLE_MESSAGE_EDGE_RULES: Record<
  VisibleMessageEdge,
  VisibleMessageEdgeRule
> = {
  [VISIBLE_MESSAGE_EDGE.Top]: {
    initialValue: Infinity,
    getCandidateValue: (rect) => rect.top,
    isBetterCandidate: (candidateValue, bestValue) => candidateValue < bestValue,
  },
  [VISIBLE_MESSAGE_EDGE.Bottom]: {
    initialValue: -Infinity,
    getCandidateValue: (rect) => rect.bottom,
    isBetterCandidate: (candidateValue, bestValue) => candidateValue > bestValue,
  },
};

export function getTopVisibleMessageId(
  input: Omit<GetVisibleMessageIdInput, "edge">,
): string | null {
  return getVisibleMessageId({
    ...input,
    edge: VISIBLE_MESSAGE_EDGE.Top,
  });
}

export function getBottomVisibleMessageId(
  input: Omit<GetVisibleMessageIdInput, "edge">,
): string | null {
  return getVisibleMessageId({
    ...input,
    edge: VISIBLE_MESSAGE_EDGE.Bottom,
  });
}

function getVisibleMessageId({
  scrollRef,
  visibleMessageIdsRef,
  messageElementsRef,
  edge,
}: GetVisibleMessageIdInput): string | null {
  const el = scrollRef.current;
  if (!el) return null;

  const containerRect = el.getBoundingClientRect();
  const rule = VISIBLE_MESSAGE_EDGE_RULES[edge];
  let bestId: string | null = null;
  let bestValue = rule.initialValue;

  visibleMessageIdsRef.current.forEach((id) => {
    const msgEl = messageElementsRef.current.get(id);
    if (!msgEl) return;

    const rect = msgEl.getBoundingClientRect();
    if (!doesRectIntersectContainer({ rect, containerRect })) return;

    const candidateValue = rule.getCandidateValue(rect);
    if (rule.isBetterCandidate(candidateValue, bestValue)) {
      bestValue = candidateValue;
      bestId = id;
    }
  });

  return bestId;
}

function doesRectIntersectContainer({
  rect,
  containerRect,
}: {
  rect: DOMRect;
  containerRect: DOMRect;
}): boolean {
  return rect.bottom > containerRect.top && rect.top < containerRect.bottom;
}
